import React from "react";
import { CSVLink } from "react-csv";
import clsx from "clsx";

import { ReactComponent as DownloadIcon } from "../../../assets/svg/download.svg";
import csvSheetImg from "../../../assets/images/csv_sheet.png";
import Spinner from "../../../atoms/Spinner";
import Sidebar from "../../../organisms/teacher/Sidebar";
import { ITeachingMaterial, ISimpleTeachingMaterial } from "../../../state";
import { CategoryType } from "../../../constants/teaching_materials";
import useCategorizeMaterials from "../../../hooks/useCategorizeMaterials";
import {
  getCurrentMaterials,
  resolveActiveTab,
} from "../../../libs/teachingMaterialUtils";
import { BA_WORK_SELECTION_LESSON_ID } from "../../../pages/teacher/DownloadCSVPage";

import styles from "./DownloadCSV.module.scss";

export type Props = {
  calling: boolean;
  sidebarMaterials: ITeachingMaterial[];
  lessonMaterials: ISimpleTeachingMaterial[];
  dataCSV: string[][];
  goBack: () => void;
  onDownLoadData: (teachingMaterialId: number, lessonId: number) => void;
};

export const DownloadCSV: React.FC<Props> = ({
  calling,
  sidebarMaterials,
  lessonMaterials,
  dataCSV,
  goBack,
  onDownLoadData,
}) => {
  const [selectedMaterialId, setSelectedMaterialId] = React.useState<
    number | null
  >(null);
  const [pendingLessonId, setPendingLessonId] = React.useState<number | null>(
    null,
  );
  const [manualTab, setManualTab] = React.useState<CategoryType | null>(null);

  const mountedRef = React.useRef(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const linkCSV = React.useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);

  React.useEffect(() => {
    if (mountedRef.current) {
      setHasLoaded(true);
    }
    mountedRef.current = true;
  }, [lessonMaterials]);

  const categorizedMaterials = useCategorizeMaterials(sidebarMaterials);

  const activeTab = React.useMemo(
    () => resolveActiveTab(categorizedMaterials, manualTab),
    [categorizedMaterials, manualTab],
  );

  const filteredSidebarMaterials = React.useMemo(
    () => getCurrentMaterials(categorizedMaterials, activeTab),
    [categorizedMaterials, activeTab],
  );

  React.useEffect(() => {
    if (filteredSidebarMaterials.length > 0) {
      setSelectedMaterialId(filteredSidebarMaterials[0].id);
    }
  }, [filteredSidebarMaterials]);

  const selectedSidebarMaterial = React.useMemo(
    () => sidebarMaterials.find(m => m.id === selectedMaterialId) ?? null,
    [sidebarMaterials, selectedMaterialId],
  );

  const selectedLessonMaterial = React.useMemo(
    () => lessonMaterials.find(m => m.id === selectedMaterialId) ?? null,
    [lessonMaterials, selectedMaterialId],
  );

  const lessonItems = React.useMemo(() => {
    if (!selectedLessonMaterial) return [];
    const lessons = selectedLessonMaterial.lessons;
    const items: { id: number; label: string }[] = [];

    if (lessons.some(l => l.one_page_portfolio_assessment_id)) {
      items.push({
        id: BA_WORK_SELECTION_LESSON_ID,
        label: "Before/After WORK",
      });
    }

    lessons
      .filter(l => l.name.trim() !== "" || l.is_leaf)
      .forEach(l => {
        const label = l.is_leaf ? "実践ワークシート" : `${l.name}  ${l.title}`;
        items.push({ id: l.id, label });
      });

    return items;
  }, [selectedLessonMaterial]);

  React.useEffect(() => {
    if (linkCSV.current && pendingLessonId !== null && dataCSV.length > 0) {
      setTimeout(() => {
        linkCSV.current?.link?.click();
      });
      setPendingLessonId(null);
    }
  }, [dataCSV, pendingLessonId]);

  const handleDownload = React.useCallback(
    (lessonId: number) => {
      if (!selectedLessonMaterial) return;
      setPendingLessonId(lessonId);
      onDownLoadData(selectedLessonMaterial.id, lessonId);
    },
    [selectedLessonMaterial, onDownLoadData],
  );

  return (
    <div className={styles.pageLayout}>
      {!hasLoaded ? (
        <div className={styles.loading}>
          <Spinner spinColor="black" />
          <p className={styles.loadingText}>教材取得中...</p>
        </div>
      ) : (
        <>
          <Sidebar
            classList={[]}
            currentMaterials={filteredSidebarMaterials}
            viewingMaterialId={selectedMaterialId}
            activeModalId={null}
            activeTab={activeTab}
            targetClassIds={[]}
            showTabs={categorizedMaterials.hasMultipleMaterialCategories}
            hasMiddle={categorizedMaterials.hasMiddle}
            hasHigh={categorizedMaterials.hasHigh}
            hasCore={categorizedMaterials.hasCore}
            hasBase={categorizedMaterials.hasBase}
            backLabel="データ選択に戻る"
            onBackToClass={goBack}
            onTabClick={tab => setManualTab(tab)}
            onItemClick={(_, materialId) => setSelectedMaterialId(materialId)}
          />

          <div className={styles.main}>
            <div className={styles.innerContent}>
              <h2 className={styles.pageTitle}>回答のCSVデータ</h2>

              <div className={styles.imageCard}>
                <img
                  src={csvSheetImg}
                  alt="回答のCSVデータ"
                  className={styles.csvImage}
                />
                <p className={styles.imageCardDesc}>
                  生徒データを細かく分析したい先生向けのCSVデータです。
                </p>
              </div>

              {selectedSidebarMaterial && (
                <>
                  <h3 className={styles.materialTitle}>
                    {selectedSidebarMaterial.name.trim()}{" "}
                    {selectedSidebarMaterial.title.replace(
                      /\s*-高校-|\s*-中学-/g,
                      "",
                    )}
                  </h3>

                  <div className={styles.lessonList}>
                    {lessonItems.map(item => (
                      <div
                        key={item.id}
                        className={clsx(
                          styles.lessonItem,
                          calling && styles.disabled,
                        )}
                        onClick={() => handleDownload(item.id)}
                      >
                        <div className={styles.downloadIconArea}>
                          <DownloadIcon className={styles.downloadIcon} />
                        </div>
                        <div className={styles.lessonTextArea}>
                          <span className={styles.lessonLabel}>
                            {item.label}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <CSVLink
                filename="download.csv"
                data={dataCSV}
                uFEFF={true}
                hidden={true}
                ref={linkCSV}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DownloadCSV;
